import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  ListChecks,
  CalendarRange,
  HelpCircle,
  LogOut,
  Palette,
  Settings,
  type LucideIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  CommandPalette,
  useCommandPaletteHotkey,
  type CommandAction,
} from "@/components/domain/CommandPalette";
import { ICONS_BY_NAV_ID } from "@/components/shell/navIcons";
import { useAuth } from "@/lib/auth/AuthContext";
import { paletteTargetsForRole, type PaletteTarget } from "@/lib/nav/paletteTargets";

interface CommandPaletteContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  /**
   * Registriert einen Bundle von Actions unter einem Token. Ein zweiter Aufruf
   * mit demselben Token ersetzt die vorherige Menge — verhindert Leaks bei
   * re-mounts oder identity-instabilen Action-Arrays.
   */
  registerActions: (token: string, actions: readonly CommandAction[]) => () => void;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

export function useCommandPalette(): CommandPaletteContextValue {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) {
    throw new Error("useCommandPalette must be used within <CommandPaletteProvider>");
  }
  return ctx;
}

// SUCHE-1 — die getippte Eingabe, fuer Server-Suchen (Kunde per
// Telefonnummer). Eigener Kontext, damit nur seine Leser bei jedem Tastendruck
// neu zeichnen — nicht die ganze App-Huelle, die `useCommandPalette` liest.
const CommandPaletteSearchContext = createContext("");

export function useCommandPaletteSearch(): string {
  return useContext(CommandPaletteSearchContext);
}

// Icons der Nav-losen Ziele — die Leisten-Blaetter tragen ihr Leisten-Icon
// (navIcons.ts), die System-Unterseiten das Zahnrad des Konto-Menues.
const ICONS_BY_TARGET_ID: Record<string, LucideIcon> = {
  "route:leads-aufraeumen": ListChecks,
  "route:finanzen-liquiditaet": CalendarRange,
  "route:hilfe": HelpCircle,
  "route:styleguide": Palette,
};

function iconFor(target: PaletteTarget): LucideIcon {
  if (target.navId) return ICONS_BY_NAV_ID[target.navId];
  return ICONS_BY_TARGET_ID[target.id] ?? Settings;
}

export function CommandPaletteProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // Frueher leerte cmdk die Eingabe selbst (der Dialog-Inhalt faellt beim
  // Schliessen weg). Seit die Eingabe hier liegt, leert sie der Provider.
  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);
  const [dynamicBundles, setDynamicBundles] = useState<ReadonlyMap<string, readonly CommandAction[]>>(
    () => new Map(),
  );

  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  // Die EFFEKTIVE Rolle: beim Vertreten liefert /api/me die Rolle des
  // Vertretenen — die Palette zeigt dann dessen Seiten, nicht die eigenen
  // (VTR-7/VTR-8). Der AuthProvider baut den Baum beim Wechsel ohnehin neu.
  const role = user?.role ?? null;

  const toggle = useCallback(() => setOpen((v) => !v), []);
  useCommandPaletteHotkey(toggle);

  const registerActions = useCallback(
    (token: string, actions: readonly CommandAction[]) => {
      setDynamicBundles((prev) => {
        const next = new Map(prev);
        next.set(token, actions);
        return next;
      });
      return () => {
        setDynamicBundles((prev) => {
          if (!prev.has(token)) return prev;
          const next = new Map(prev);
          next.delete(token);
          return next;
        });
      };
    },
    [],
  );

  // NAV-4: «Gehe zu <Seite>» fuer jede Seite, die die Rolle oeffnen darf —
  // Quelle ist die Leiste (navItemsForRole) plus die Nav-losen Ziele mit
  // ihren Route-Praedikaten (paletteTargets.ts). Reihenfolge = Leiste, dann
  // die Nav-losen; die Tipp-Suche darf umsortieren.
  const goToActions = useMemo<readonly CommandAction[]>(() => {
    if (!role) return [];
    return paletteTargetsForRole(role).map((target) => ({
      id: target.id,
      label: target.label,
      description: target.description,
      group: "Gehe zu",
      icon: iconFor(target),
      keywords: target.keywords,
      onSelect: () => {
        void navigate(target.to);
      },
    }));
  }, [navigate, role]);

  const sessionActions = useMemo<readonly CommandAction[]>(
    () => [
      {
        id: "session:signout",
        label: "Abmelden",
        group: "Session",
        icon: LogOut,
        onSelect: () => {
          void signOut();
        },
      },
    ],
    [signOut],
  );

  // Gruppen-Reihenfolge = Reihenfolge des ersten Auftretens: Gehe zu ·
  // Aktionen · Kunden · Projekte · Session (Abmelden zuletzt, Prinzip 9).
  const actions = useMemo(() => {
    const all: CommandAction[] = [...goToActions];
    for (const bundle of dynamicBundles.values()) all.push(...bundle);
    all.push(...sessionActions);
    return all;
  }, [goToActions, dynamicBundles, sessionActions]);

  const value = useMemo<CommandPaletteContextValue>(
    () => ({ open, setOpen, toggle, registerActions }),
    [open, toggle, registerActions],
  );

  return (
    <CommandPaletteContext.Provider value={value}>
      <CommandPaletteSearchContext.Provider value={search}>
        {children}
        <CommandPalette
          open={open}
          onOpenChange={setOpen}
          actions={actions}
          search={search}
          onSearchChange={setSearch}
        />
      </CommandPaletteSearchContext.Provider>
    </CommandPaletteContext.Provider>
  );
}
